import { inject, Injectable } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { BaseProfileService } from "./base-profile-service";
import { ISensor } from "../../mobile-models/profile-models";
import { UsersService, UserViewModel } from 'app/services/users.service';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class LocalizationService {
  private fuelUnits: string = 'lt';
  private translateService: TranslateService = inject(TranslateService);
  private baseProfileService: BaseProfileService = inject(BaseProfileService);
  private usersService: UsersService = inject(UsersService);
  currentUser: UserViewModel;

  constructor() {
    this.initFuelUnits();
    this.currentUser = this.usersService.getCurrentUser();
  }

  private initFuelUnits(): void {
    this.baseProfileService.getObjects()
      .forEach(obj => {
        const sensor: ISensor = obj.sensors.find(s => s.flags.indexOf('FUEL') >= 0);
        if (sensor) {
          this.fuelUnits = sensor.units;
        }
      })
  }

  public IsMetric(): boolean { return !this.currentUser.HasFlag('MILES'); }
  public getUnits(): string {
    return this.IsMetric() ? 'm' : 'ft';
  }

  get units(): string { return this.getUnits(); }

  public getSpeed(speed: number, withoutunits?: boolean): string {
    return this.getSpeedValue(speed) + (withoutunits ? '' : ' ' + this.getSpeedUnits());
  }

  public getSpeedUnits(): string {
    switch (this.units) {
      case 'ft':
        return this.translateService.instant('mi/h');
      default:
        return this.translateService.instant('km/h');
    }
  }

  private getSpeedValue(speed: number, param?: any): number {
    param = param || {};
    switch (this.units) {
      case 'ft':
        return Math.round(speed / (param.toMeters ? 0.621371 : 1.6093));
      default:
        return Math.round(speed);
    }
  }

  public getLengthUnits(): string {
    switch (this.units) {
      case 'ft':
        return this.translateService.instant('mi');
      default:
        return this.translateService.instant('km');
    }
  }

  public getLength(length: number): string {
    switch (this.units) {
      case 'ft': {
        length *= 3.28084;
        return (length >= 5280 || true)
          ? (length * 0.000189394).toFixed(2).replace('.00', '') + ' ' + this.translateService.instant('mi')
          : Math.round(length) + ' ' + this.translateService.instant('ft');
      }
      default:
        return (length >= 1000 || true)
          ? (length * 0.001).toFixed(2).replace('.00', '') + ' ' + this.translateService.instant('km')
          : Math.round(length) + ' ' + this.translateService.instant('m');
    }
  }

  private getLengthValue(length: number, param?: any): number {
    param = param || {};
    if (param.toMeters) {
      switch (this.units) {
        case 'ft':
          return param.km ? (length * 1609.343) : Math.round(length / 3.28084);
        default:
          return param.km ? (length * 1000.0) : Math.round(length);
      }
    } else {
      switch (this.units) {
        case 'ft': {
          length *= 3.28084;
          return param.km ? (length * 0.000189394) : Math.round(length);
        }
        default:
          return param.km ? (length * 0.001) : Math.round(length);
      }
    }
  }

  public fromMeters(val: number): number {
    return this.getLengthValue(val);
  }

  public fromMetersToKM(val: number): number {
    return this.getLengthValue(val, { km: true });
  }

  public toMeters(val: number): number {
    return this.getLengthValue(val, { toMeters: true });
  }

  public toMetersFromKM(val: number): number {
    return this.getLengthValue(val, { toMeters: true, km: true });
  }

  public toSpeedUnits(val: number): number {
    return this.getSpeedValue(val);
  }

  public fromSpeedUnits(val: number): number {
    return this.getSpeedValue(val, { toMeters: true });
  }

  public getArea(length: number): string {
    switch (this.units) {
      case 'ft': {
        length *= 10.7639111056;
        return ((length >= 27878400)
          ? (length * 3.5870087236e-8).toFixed(2) + ' ' + this.translateService.instant('mi')
          : length.toFixed(0) + ' ' + this.translateService.instant('ft')) + '<sup>2</sup>';
      }
      default:
        return ((length >= 1000000)
          ? (length * 0.000001).toFixed(2) + ' ' + this.translateService.instant('km')
          : (length * 1.0).toFixed(0) + ' ' + this.translateService.instant('m')) + '<sup>2</sup>';
    }
  }

  public getFuelUnits(): string {
    return this.translateService.instant(this.fuelUnits);
  }

  //liters and galons
  // public getVolume(liters: number): string {
  //   switch (this.units) {
  //     case 'ft': { // imperial system
  //       const gallons = liters * 0.264172; // литры → галлоны
  //       if (gallons >= 128) {
  //         const barrels = Math.round(gallons / 128);
  //         return barrels + ' ' + this.translateService.instant('bbl');
  //       } else {
  //         return Math.round(gallons) + ' ' + this.translateService.instant('gal');
  //       }
  //     }

  //     default: { // metric system
  //       if (liters >= 1000) {
  //         const cubicMeters = Math.round(liters / 1000);
  //         return cubicMeters + ' ' + this.translateService.instant('m³');
  //       } else {
  //         return Math.round(liters) + ' ' + this.translateService.instant('L');
  //       }
  //     }
  //   }
  // }


  public getDuration(from: Date, to: Date): string {
    const diffMs = Math.abs(to.getTime() - from.getTime());

    return this.getDurationFromPeriod(diffMs);
  }

  public getDurationFromPeriod(miliSeconds: number): string {
    const minutes = Math.floor(miliSeconds / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    const tDays = this.translateService.instant('days');
    const tHours = this.translateService.instant('h');
    const tMinutes = this.translateService.instant('min');
    const tSeconds = this.translateService.instant('sec');

    if (days > 0) {
      const remainingHours = hours % 24;
      return `${days} ${tDays} ${remainingHours} ${tHours}`;
    } else if (hours > 0) {
      const remainingMinutes = minutes % 60;
      return `${hours} ${tHours} ${remainingMinutes} ${tMinutes}`;
    } else if (minutes > 0) {
      return `${minutes} ${tMinutes}`;
    } else {
      const secondsLeft = Math.floor(miliSeconds / 1000);
      return `${secondsLeft} ${tSeconds}`;
    }
  }

  public getTimeZoneOffsetMs(utcMs: number, timeZone: string): number {

    // Build Y/M/D/h/m/s as seen in the target time zone
    const d = new Date(utcMs);
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      calendar: 'gregory',
      numberingSystem: 'latn',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(d);

    const get = (t: string) => Number(parts.find(p => p.type === t)!.value);
    const y = get('year'), m = get('month'), day = get('day');
    const h = get('hour'), min = get('minute'), s = get('second');

    // "asIfUTC" = interpret those local parts as if they were UTC
    const asIfUtcMs = Date.UTC(y, m - 1, day, h, min, s);

    // Offset = (localParts interpreted as UTC) - actual UTC instant
    // For UTC+5, this will be +5h * 3600_000
    return asIfUtcMs - utcMs;
  }

  //just show time,do not consider timezone
  public formatTime(
    timestamp: Date,
    format: 'H:mm' | 'H:mm:ss' = 'H:mm'
  ): string {

    let date = new Date(timestamp);

    // Intl formatting options — force UTC
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC', // <-- key part
    };

    if (format === 'H:mm:ss') {
      options.second = '2-digit';
    }

    return new Intl.DateTimeFormat("en-US", options).format(date);
  }

  public convertTimeZoneOffsetToUtcMsForUserDisplay(dateUtc: number, userSelectedTimeZone: string): Date {
    if (!dateUtc) {
      console.error("DateUTC is null")
      return new Date(0); // Unix epoch - January 1, 1970
    }
    let utcMs = dateUtc;

    const offsetMs = this.getTimeZoneOffsetMs(utcMs, userSelectedTimeZone);
    return new Date(utcMs + offsetMs);
  }


  public convertTimeZoneOffsetToUtcMsForServer(local: Date, timeZone: string): number {
    // let newYork = moment.tz(local, "America/New_York");

    // newYork.toDate();
    const y = local.getFullYear();
    const m = local.getMonth() + 1;
    const d = local.getDate();
    const hh = local.getHours();
    const mm = local.getMinutes();
    const ss = local.getSeconds();
    const ms = local.getMilliseconds();

    const guessUtcMs = Date.UTC(y, m - 1, d, hh, mm, ss, ms);

    // 2-pass refinement (DST-safe)
    let offsetMs = this.getTimeZoneOffsetMs(guessUtcMs, timeZone);
    let utcMs = guessUtcMs - offsetMs;

    offsetMs = this.getTimeZoneOffsetMs(utcMs, timeZone);
    utcMs = guessUtcMs - offsetMs;

    return utcMs;
  }
}
